import { useState } from 'react'

export interface FormData {
  // Homepage hero fields
  degreeInterest: string
  areaOfStudy: string
  specialization: string
  // University form step 1
  currentEducation: string
  startDate: string
  gradYear: string
  // University form step 2
  zipCode: string
  city: string
  state: string
  address: string
  // University form step 3 / expanded form
  firstName: string
  lastName: string
  email: string
  phone: string
  tcpaConsent: boolean
  // Expanded form extras
  isUSCitizen: string
  isMilitary: string
}

const initialFormData: FormData = {
  degreeInterest: '',
  areaOfStudy: '',
  specialization: '',
  currentEducation: '',
  startDate: '',
  gradYear: '',
  zipCode: '',
  city: '',
  state: '',
  address: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  tcpaConsent: false,
  isUSCitizen: '',
  isMilitary: '',
}

export function useFormState(prefill?: Partial<FormData>) {
  const [formData, setFormData] = useState<FormData>({
    ...initialFormData,
    ...prefill,
  })
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {},
  )

  const updateFormData = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const updateMultiple = (updates: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }))
  }

  const validateHeroForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {}
    if (!formData.degreeInterest)
      newErrors.degreeInterest = 'Please select a degree level.'
    if (!formData.areaOfStudy)
      newErrors.areaOfStudy = 'Please select an area of study.'
    if (!formData.specialization)
      newErrors.specialization = 'Please select a program.'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateUniStep1 = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {}
    if (!formData.currentEducation)
      newErrors.currentEducation = 'Please select your education level.'
    if (!formData.startDate)
      newErrors.startDate = 'Please select when you want to start.'
    if (!formData.gradYear)
      newErrors.gradYear = 'Please select your high school graduation year.'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateUniStep2 = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {}
    if (
      !formData.zipCode.trim() ||
      formData.zipCode.replace(/\D/g, '').length < 5
    ) {
      newErrors.zipCode = 'Please enter a valid 5-digit zip code.'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateUniStep3 = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {}
    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required.'
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required.'
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'A valid email address is required.'
    }
    if (
      !formData.phone.trim() ||
      formData.phone.replace(/\D/g, '').length < 10
    ) {
      newErrors.phone = 'A valid 10-digit phone number is required.'
    }
    if (!formData.tcpaConsent)
      newErrors.tcpaConsent = 'You must agree to the terms to continue.'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateExpandedForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {}
    if (!formData.currentEducation)
      newErrors.currentEducation = 'Please select your education level.'
    if (!formData.isUSCitizen)
      newErrors.isUSCitizen = 'Please select an option.'
    if (!formData.isMilitary) newErrors.isMilitary = 'Please select an option.'
    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required.'
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required.'
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'A valid email address is required.'
    }
    if (
      !formData.phone.trim() ||
      formData.phone.replace(/\D/g, '').length < 10
    ) {
      newErrors.phone = 'A valid 10-digit phone number is required.'
    }
    if (
      !formData.zipCode.trim() ||
      formData.zipCode.replace(/\D/g, '').length < 5
    ) {
      newErrors.zipCode = 'A valid 5-digit zip code is required.'
    }
    if (!formData.tcpaConsent)
      newErrors.tcpaConsent = 'You must agree to the terms to continue.'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const resetForm = () => {
    setFormData(initialFormData)
    setErrors({})
  }

  return {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateHeroForm,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3,
    validateExpandedForm,
    resetForm,
  }
}
